import React from 'react';

import {
  IconApi,
  IconCalendarCheck,
  IconCalendarClock,
  IconMailForward,
  IconMessageCircleUp,
  IconPhoneOutgoing,
  IconUsers
} from '@tabler/icons-react';
import { UserSwitchIcon } from 'hugeicons-react';
import i18n from 'i18n';
import { ProductName } from 'shared/interfaces/Product';
import { SmartAssistantType } from 'shared/interfaces/SmartAssistant';
import { StaticToolGroup, ToolGroupKind, ToolKind } from 'shared/interfaces/tools/Tool';

export const LeadCollectionStaticToolGroup: StaticToolGroup = {
  kind: ToolGroupKind.LEAD_COLLECTION,
  toolKinds: [ToolKind.COLLECT_LEAD_INFO],
  minProduct: ProductName.BASIC,
  displayName: i18n.t('SMART_ASSISTANT.TOOLS.LEAD_COLLECTION.TITLE'),
  displayDescription: i18n.t('SMART_ASSISTANT.TOOLS.LEAD_COLLECTION.INFO'),
  icon: <IconUsers />
};

export const CalendlyAppointmentSchedulingStaticToolGroup: StaticToolGroup = {
  kind: ToolGroupKind.CALENDLY,
  toolKinds: [ToolKind.CALENDLY],
  minProduct: ProductName.BASIC,
  displayName: i18n.t('SMART_ASSISTANT.TOOLS.CALENDLY.TITLE'),
  displayDescription: i18n.t('SMART_ASSISTANT.TOOLS.CALENDLY.INFO'),
  icon: <IconCalendarClock />
};

export const EmailHandoffStaticToolGroup: StaticToolGroup = {
  kind: ToolGroupKind.EMAIL_HANDOFF,
  toolKinds: [ToolKind.EMAIL_HANDOFF],
  minProduct: ProductName.ADVANCED,
  displayName: i18n.t('SMART_ASSISTANT.TOOLS.EMAIL_HANDOFF.TITLE'),
  displayDescription: i18n.t('SMART_ASSISTANT.TOOLS.EMAIL_HANDOFF.INFO'),
  icon: <IconMailForward />
};

export const TakeoverRequestStaticToolGroup: StaticToolGroup = {
  kind: ToolGroupKind.TAKEOVER_REQUEST,
  toolKinds: [ToolKind.TAKEOVER_REQUEST],
  minProduct: ProductName.ADVANCED,
  displayName: i18n.t('SMART_ASSISTANT.TOOLS.TAKEOVER_REQUEST.TITLE'),
  displayDescription: i18n.t('SMART_ASSISTANT.TOOLS.TAKEOVER_REQUEST.INFO'),
  icon: <UserSwitchIcon />
};

export const FollowUpStaticToolGroup: StaticToolGroup = {
  kind: ToolGroupKind.FOLLOW_UP,
  toolKinds: [ToolKind.FOLLOW_UP],
  minProduct: ProductName.ADVANCED,
  displayName: i18n.t('SMART_ASSISTANT.TOOLS.FOLLOW_UP.TITLE'),
  displayDescription: i18n.t('SMART_ASSISTANT.TOOLS.FOLLOW_UP.INFO'),
  icon: <IconMessageCircleUp />
};

export const CaldotcomAppointmentSchedulingStaticToolGroup: StaticToolGroup = {
  kind: ToolGroupKind.CALDOTCOM_APPOINTMENT_SCHEDULING,
  toolKinds: [ToolKind.CALDOTCOM_SCHEDULE_MEETING, ToolKind.CALDOTCOM_CHECK_AVAILABILITY],
  minProduct: ProductName.STARTER,
  displayName: i18n.t('SMART_ASSISTANT.TOOLS.CALDOTCOM_APPOINTMENT_SCHEDULING.TITLE'),
  displayDescription: i18n.t('SMART_ASSISTANT.TOOLS.CALDOTCOM_APPOINTMENT_SCHEDULING.INFO'),
  icon: <IconCalendarCheck style={{ width: '24px', height: '24px' }} />
};

export const CallTransferStaticToolGroup: StaticToolGroup = {
  kind: ToolGroupKind.CALL_TRANSFER,
  toolKinds: [ToolKind.CALL_TRANSFER],
  minProduct: ProductName.STARTER,
  displayName: i18n.t('SMART_ASSISTANT.TOOLS.CALL_TRANSFER.TITLE'),
  displayDescription: i18n.t('SMART_ASSISTANT.TOOLS.CALL_TRANSFER.INFO'),
  icon: <IconPhoneOutgoing />
};

export const CustomApiRequestStaticToolGroup: StaticToolGroup = {
  kind: ToolGroupKind.CUSTOM_API_REQUEST,
  toolKinds: [ToolKind.CUSTOM_API_REQUEST],
  minProduct: ProductName.ADVANCED,
  displayName: i18n.t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.TITLE'),
  displayDescription: i18n.t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.INFO'),
  icon: <IconApi />
};

export const ALL_TOOL_GROUPS = [
  LeadCollectionStaticToolGroup,
  CalendlyAppointmentSchedulingStaticToolGroup,
  EmailHandoffStaticToolGroup,
  TakeoverRequestStaticToolGroup,
  FollowUpStaticToolGroup,
  CaldotcomAppointmentSchedulingStaticToolGroup,
  CallTransferStaticToolGroup,
  CustomApiRequestStaticToolGroup
] as const;

// Tool groups that can have multiple instances of the same tool kind
export const MULTI_INSTANCE_TOOL_GROUP_KINDS = [ToolGroupKind.CUSTOM_API_REQUEST];

export const SMART_ASSISTANT_CONFIGURABLE_TOOL_GROUPS_BY_TYPE: Record<SmartAssistantType, StaticToolGroup[]> = {
  [SmartAssistantType.BASIC]: [CustomApiRequestStaticToolGroup],
  [SmartAssistantType.WEBSITE]: [
    LeadCollectionStaticToolGroup,
    CalendlyAppointmentSchedulingStaticToolGroup,
    EmailHandoffStaticToolGroup,
    CustomApiRequestStaticToolGroup
  ],
  [SmartAssistantType.SHOPIFY]: [
    LeadCollectionStaticToolGroup,
    CalendlyAppointmentSchedulingStaticToolGroup,
    EmailHandoffStaticToolGroup,
    CustomApiRequestStaticToolGroup
  ],
  [SmartAssistantType.DISCORD]: [
    LeadCollectionStaticToolGroup,
    CalendlyAppointmentSchedulingStaticToolGroup,
    EmailHandoffStaticToolGroup,
    CustomApiRequestStaticToolGroup
  ],
  [SmartAssistantType.INSTAGRAM]: [
    TakeoverRequestStaticToolGroup,
    FollowUpStaticToolGroup,
    CustomApiRequestStaticToolGroup
  ],
  [SmartAssistantType.FACEBOOK]: [
    TakeoverRequestStaticToolGroup,
    FollowUpStaticToolGroup,
    CustomApiRequestStaticToolGroup
  ],
  [SmartAssistantType.WHATSAPP]: [
    TakeoverRequestStaticToolGroup,
    FollowUpStaticToolGroup,
    CustomApiRequestStaticToolGroup
  ],
  [SmartAssistantType.PHONE]: [
    CaldotcomAppointmentSchedulingStaticToolGroup,
    CallTransferStaticToolGroup,
    CustomApiRequestStaticToolGroup
  ],
  [SmartAssistantType.BIGCOMMERCE]: [],
  [SmartAssistantType.WIX]: [],
  [SmartAssistantType.WOOCOMMERCE]: [],
  [SmartAssistantType.SQUARESPACE]: [],
  [SmartAssistantType.SMS]: []
};
